/**
 * AddressForm - Reusable US mailing address form
 *
 * Pink-themed inputs, client-side validation, US state dropdown.
 * Used in both the EnvelopeModal and AccountPage edit flow.
 */

import { useState } from 'react'
import type { AddressData } from './mailingService'

const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'Washington D.C.' }
]

interface AddressFormProps {
  initialData?: Partial<AddressData>
  onSubmit: (data: AddressData) => void
  onCancel?: () => void
  submitLabel?: string
  isSubmitting?: boolean
}

export function AddressForm({
  initialData,
  onSubmit,
  onCancel,
  submitLabel = 'Continue',
  isSubmitting = false
}: AddressFormProps) {
  const [fullName, setFullName] = useState(initialData?.full_name || '')
  const [addressLine1, setAddressLine1] = useState(initialData?.address_line1 || '')
  const [addressLine2, setAddressLine2] = useState(initialData?.address_line2 || '')
  const [city, setCity] = useState(initialData?.city || '')
  const [state, setState] = useState(initialData?.state || '')
  const [zipCode, setZipCode] = useState(initialData?.zip_code || '')
  const [errors, setErrors] = useState<Record<string, string>>({})

  function validate(): boolean {
    const newErrors: Record<string, string> = {}

    if (!fullName.trim()) newErrors.full_name = 'Name is required'
    if (!addressLine1.trim()) newErrors.address_line1 = 'Address is required'
    if (!city.trim()) newErrors.city = 'City is required'
    if (!state) newErrors.state = 'State is required'
    if (!zipCode.trim()) {
      newErrors.zip_code = 'ZIP code is required'
    } else if (!/^\d{5}(-\d{4})?$/.test(zipCode.trim())) {
      newErrors.zip_code = 'Enter a valid ZIP code (12345 or 12345-6789)'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return

    onSubmit({
      full_name: fullName.trim(),
      address_line1: addressLine1.trim(),
      address_line2: addressLine2.trim() || undefined,
      city: city.trim(),
      state: state.toUpperCase(),
      zip_code: zipCode.trim()
    })
  }

  const inputClass = 'w-full px-3 py-2 bg-white border border-pink-200 rounded-lg text-sm focus:outline-none focus:border-pink-400 focus:ring-1 focus:ring-pink-300 transition-colors'
  const labelClass = 'block text-xs font-medium text-gray-700 mb-1'
  const errorClass = 'text-xs text-red-500 mt-1'

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Full Name */}
      <div>
        <label className={labelClass}>Full Name</label>
        <input
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="John Doe"
          className={inputClass}
          disabled={isSubmitting}
        />
        {errors.full_name && <p className={errorClass}>{errors.full_name}</p>}
      </div>

      {/* Address Line 1 */}
      <div>
        <label className={labelClass}>Street Address</label>
        <input
          type="text"
          value={addressLine1}
          onChange={(e) => setAddressLine1(e.target.value)}
          placeholder="123 Main Street"
          className={inputClass}
          disabled={isSubmitting}
        />
        {errors.address_line1 && <p className={errorClass}>{errors.address_line1}</p>}
      </div>

      {/* Address Line 2 */}
      <div>
        <label className={labelClass}>Apt, Suite, Unit <span className="text-gray-400">(optional)</span></label>
        <input
          type="text"
          value={addressLine2}
          onChange={(e) => setAddressLine2(e.target.value)}
          placeholder="Apt 4B"
          className={inputClass}
          disabled={isSubmitting}
        />
      </div>

      {/* City */}
      <div>
        <label className={labelClass}>City</label>
        <input
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="New York"
          className={inputClass}
          disabled={isSubmitting}
        />
        {errors.city && <p className={errorClass}>{errors.city}</p>}
      </div>

      {/* State + ZIP */}
      <div className="flex gap-3">
        <div className="flex-1">
          <label className={labelClass}>State</label>
          <select
            value={state}
            onChange={(e) => setState(e.target.value)}
            className={inputClass}
            disabled={isSubmitting}
          >
            <option value="">Select state</option>
            {US_STATES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          {errors.state && <p className={errorClass}>{errors.state}</p>}
        </div>
        <div className="w-32">
          <label className={labelClass}>ZIP Code</label>
          <input
            type="text"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            placeholder="10001"
            maxLength={10}
            className={inputClass}
            disabled={isSubmitting}
          />
          {errors.zip_code && <p className={errorClass}>{errors.zip_code}</p>}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-2 px-4 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="flex-1 py-2 px-4 text-sm font-medium text-white bg-pink-500 rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Validating...' : submitLabel}
        </button>
      </div>
    </form>
  )
}
